<script setup>
import { computed, ref } from 'vue'
import { defineProps, defineEmits } from 'vue'
import { useNotificationStore } from '../stores/notificationStore'
// Removed announcement banner in favor of Events-only overlay

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const notificationStore = useNotificationStore()
const activeTab = ref('updates')

const tabs = [
  // { id: 'all', label: 'All', icon: 'fa-bell' },
  // { id: 'games', label: 'Games', icon: 'fa-basketball-ball' },
  // { id: 'system', label: 'System', icon: 'fa-cog' },
  // { id: 'purchases', label: 'Purchases', icon: 'fa-shopping-cart' },
  // { id: 'alerts', label: 'Alerts', icon: 'fa-exclamation-circle' },
  { id: 'updates', label: 'Updates', icon: 'fa-bullhorn' },
]

const notifications = computed(() => {
  const allNotifications = notificationStore.notifications
  if (activeTab.value === 'all') return allNotifications
  if (activeTab.value === 'updates') return notificationStore.updates || []
  return allNotifications.filter(n => n.category === activeTab.value)
})

const groupedNotifications = computed(() => {
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  }

  notifications.value.forEach(notification => {
    // In real app, use actual date comparison
    if (notification.time.includes('min ago') || notification.time.includes('Just now')) {
      groups.today.push(notification)
    } else if (notification.time.includes('day ago')) {
      groups.yesterday.push(notification)
    } else if (notification.time.includes('days ago') && parseInt(notification.time) <= 7) {
      groups.thisWeek.push(notification)
    } else {
      groups.older.push(notification)
    }
  })

  return groups
})

const unreadCount = computed(() => {
  if (activeTab.value === 'updates') {
    return (notificationStore.updates || []).filter(u => !u.read).length
  }
  return notifications.value.filter(n => !n.read).length
})

const markAsRead = (notification) => {
  notificationStore.markAsRead(notification.id)
}

const markAllAsRead = () => {
  notificationStore.markAllAsRead()
}

const clearAllNotifications = () => {
  notificationStore.clearAllNotifications()
}
</script>

<template>
  <transition
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div 
        class="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full h-[92vh] sm:h-[94vh] flex"
      >
        <!-- Sidebar Tabs -->
        <aside class="hidden sm:flex sm:flex-col w-48 border-r dark:border-gray-700 p-4 space-y-1">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="w-full text-left px-3 py-2 rounded-md transition-colors"
            :class="[
              activeTab === tab.id
                ? 'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-300'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            ]"
          >
            <i :class="['fas', tab.icon, 'mr-2']"></i>
            {{ tab.label }}
          </button>
        </aside>

        <div class="flex-1 flex flex-col min-w-0">
          <!-- Header -->
          <div class="flex items-center justify-between p-4 sm:p-6 border-b dark:border-gray-700">
          <div>
            <h2 class="text-2xl font-bold dark:text-white">Notifications</h2>
            <p class="text-gray-500 dark:text-gray-400">
              {{ unreadCount }} unread notifications
            </p>
          </div>
          <div class="flex items-center gap-4">
            <button 
              @click="markAllAsRead"
              class="text-sm text-pink-500 hover:text-pink-600 dark:text-pink-400"
            >
              Mark all as read
            </button>
            <button 
              @click="clearAllNotifications"
              class="text-sm text-gray-500 hover:text-gray-600 dark:text-gray-400"
            >
              Clear all
            </button>
            <button 
              @click="$emit('close')"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-4 sm:p-6">
          <div class="space-y-8">
            <!-- Today's Notifications -->
            <div v-if="groupedNotifications.today.length">
              <h3 class="text-lg font-medium mb-4 dark:text-white flex items-center gap-2">
                <i class="fas fa-calendar-day"></i>
                Today
              </h3>
              <div class="space-y-4">
                <div 
                  v-for="notification in groupedNotifications.today" 
                  :key="notification.id"
                  class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-all duration-200 cursor-pointer"
                  :class="{ 
                    'opacity-75': notification.read,
                    'hover:shadow-md': !notification.read
                  }"
                  @click="markAsRead(notification)"
                >
                  <div class="flex items-start gap-4">
                    <div 
                      class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      :class="{
                        'bg-blue-100 text-blue-500 dark:bg-blue-900 dark:text-blue-300': notification.type === 'info',
                        'bg-green-100 text-green-500 dark:bg-green-900 dark:text-green-300': notification.type === 'success',
                        'bg-yellow-100 text-yellow-500 dark:bg-yellow-900 dark:text-yellow-300': notification.type === 'warning',
                        'bg-red-100 text-red-500 dark:bg-red-900 dark:text-red-300': notification.type === 'error'
                      }"
                    >
                      <i 
                        class="fas text-lg"
                        :class="{
                          'fa-info': notification.type === 'info',
                          'fa-check': notification.type === 'success',
                          'fa-exclamation': notification.type === 'warning',
                          'fa-times': notification.type === 'error'
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <p class="text-gray-900 dark:text-white">{{ notification.message }}</p>
                      
                      <!-- Purchase Details -->
                      <div v-if="notification.category === 'purchases' && notification.details" 
                           class="mt-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                        <div class="space-y-2">
                          <!-- Client Info -->
                          <div class="flex items-center gap-2">
                            <i class="fas fa-user text-gray-400"></i>
                            <div>
                              <div class="text-sm font-medium">{{ notification.details.client.name }}</div>
                              <div class="text-xs text-gray-500">{{ notification.details.client.email }}</div>
                            </div>
                          </div>

                          <!-- Game Info -->
                          <div class="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                            <div class="grid grid-cols-2 gap-4">
                              <div>
                                <div class="text-xs text-gray-500 mb-1">Game</div>
                                <div class="text-sm font-medium">
                                  {{ notification.details.game.teams.home }} vs {{ notification.details.game.teams.away }}
                                </div>
                              </div>
                              <div>
                                <div class="text-xs text-gray-500 mb-1">League</div>
                                <div class="text-sm font-medium">{{ notification.details.game.league }}</div>
                              </div>
                              <div>
                                <div class="text-xs text-gray-500 mb-1">Game Start</div>
                                <div class="text-sm font-medium">
                                  {{ new Date(notification.details.game.startTime).toLocaleString() }}
                                </div>
                              </div>
                              <div>
                                <div class="text-xs text-gray-500 mb-1">Purchase Amount</div>
                                <div class="text-sm font-medium text-green-600">
                                  ${{ notification.details.game.price }}
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Purchase Time -->
                          <div class="text-xs text-gray-500 mt-2">
                            Purchased: {{ new Date(notification.details.purchaseTime).toLocaleString() }}
                          </div>
                        </div>
                      </div>

                      <!-- Market Changes Details -->
                      <div v-if="notification.category === 'games' && notification.details?.markets" 
                           class="mt-2 space-y-2">
                        <div class="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          {{ notification.details.player }} - Market Changes
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                          <div v-for="market in notification.details.markets" 
                               :key="market.type"
                               class="bg-gray-50 dark:bg-gray-800 rounded p-2"
                          >
                            <div class="flex items-center justify-between">
                              <span class="text-sm font-medium">{{ market.type }}</span>
                              <div class="flex items-center gap-2">
                                <span class="text-gray-500 line-through text-sm">
                                  {{ market.old }}
                                </span>
                                <i :class="[
                                  'fas',
                                  market.trend === 'up' ? 'fa-arrow-up text-green-500' : 'fa-arrow-down text-red-500'
                                ]"></i>
                                <span :class="[
                                  'font-medium',
                                  market.significant ? 'text-pink-500' : 'text-gray-900 dark:text-white'
                                ]">
                                  {{ market.new }}
                                </span>
                              </div>
                            </div>
                            <!-- Significant Movement Indicator -->
                            <div v-if="market.significant" 
                                 class="mt-1 text-xs text-pink-500 flex items-center gap-1"
                            >
                              <i class="fas fa-exclamation-circle"></i>
                              Significant movement
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- SSE Notification Details -->
                      <div v-if="notification.source === 'sse'" class="mt-3 bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                        <div class="flex items-center gap-2 mb-2">
                          <i class="fas fa-broadcast-tower text-green-600 dark:text-green-400"></i>
                          <span class="text-sm font-medium text-green-800 dark:text-green-300">Live Notification</span>
                          <span class="text-xs text-green-600 dark:text-green-400">SSE ID: {{ notification.id }}</span>
                        </div>
                        <div v-if="notification.details && Object.keys(notification.details).length > 0" class="text-xs text-green-700 dark:text-green-300">
                          <details class="cursor-pointer">
                            <summary class="font-medium">Additional Data</summary>
                            <pre class="mt-1 text-xs overflow-auto bg-white dark:bg-gray-800 p-2 rounded border">{{ JSON.stringify(notification.details, null, 2) }}</pre>
                          </details>
                        </div>
                      </div>

                      <div class="flex items-center gap-4 mt-3">
                        <div class="flex items-center gap-2">
                          <p class="text-sm text-gray-500 dark:text-gray-400">{{ notification.time }}</p>
                          <span 
                            v-if="notification.source === 'sse'"
                            class="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                          >
                            <i class="fas fa-circle text-[6px]"></i>
                            Live
                          </span>
                        </div>
                        <span 
                          class="text-xs px-2 py-1 rounded-full"
                          :class="{
                            'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300': notification.category === 'games',
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300': notification.category === 'system',
                            'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300': notification.category === 'purchases',
                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300': notification.category === 'alerts',
                            'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300': notification.source === 'sse'
                          }"
                        >
                          {{ notification.source === 'sse' ? 'SSE' : notification.category }}
                        </span>
                        <!-- Notification Type Badge -->
                        <span 
                          v-if="notification.type"
                          class="text-xs px-2 py-1 rounded-full font-medium"
                          :class="{
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300': notification.type === 'success',
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300': notification.type === 'warning',
                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300': notification.type === 'error',
                            'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300': notification.type === 'info'
                          }"
                        >
                          {{ notification.type.toUpperCase() }}
                        </span>
                      </div>
                    </div>
                    <div 
                      v-if="!notification.read"
                      class="w-2 h-2 rounded-full bg-pink-500"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Yesterday's Notifications -->
            <template v-if="groupedNotifications.yesterday.length">
              <!-- Similar structure as Today's notifications -->
            </template>

            <!-- This Week's Notifications -->
            <template v-if="groupedNotifications.thisWeek.length">
              <!-- Similar structure -->
            </template>

            <!-- Older Notifications -->
            <template v-if="groupedNotifications.older.length">
              <!-- Similar structure -->
            </template>

            <!-- Empty State -->
            <div 
              v-if="!notifications.length"
              class="text-center py-12"
            >
              <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <i class="fas fa-bell text-gray-400 text-2xl"></i>
              </div>
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No notifications
              </h3>
              <p class="text-gray-500 dark:text-gray-400">
                You're all caught up! Check back later for new updates.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </transition>
</template> 